import type { App, InjectionKey, Plugin } from 'vue';
import { createApp, getCurrentInstance, inject } from 'vue';
import PromptPluginContainer from './PromptPluginContainer.vue';
import { EVENTS, globalEventBus } from './eventBus';
import type { PromptOptions } from './interfaces';

export interface PromptInterface {
  confirm: (opts: string | PromptOptions) => Promise<boolean>;
  alert: (opts: string | PromptOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string>;
}

export const PROMPT_INJECTION_KEY: InjectionKey<PromptInterface> = Symbol('PROMPT_PLUGIN');

export declare type PluginOptions = {
  configure?: (app: App) => void;
};

export const PromptPlugin: Plugin = (app: App, options: PluginOptions) => {
  const el = document.createElement('div');
  document.body.appendChild(el);
  const { app: pluginApp, plugin } = mountPlugin(el);
  if (options.configure) {
    options.configure(pluginApp);
  }
  app.provide(PROMPT_INJECTION_KEY, plugin);
};

const createPromptInstance = (): PromptInterface => ({
  confirm: (options: string | PromptOptions) => {
    if (typeof options === 'string') options = { message: options };
    globalEventBus.emit(EVENTS.PROMPT, { ...options, type: 'confirm' });
    return new Promise((resolve) => {
      globalEventBus.on(EVENTS.FINISH_CONFIRM, (result) => resolve(result.result));
    });
  },
  alert: (options: string | PromptOptions) => {
    if (typeof options === 'string') options = { message: options };
    globalEventBus.emit(EVENTS.PROMPT, { ...options, type: 'alert' });
    return new Promise((resolve) => {
      globalEventBus.on(EVENTS.FINISH_CONFIRM, (result) => resolve(result.result));
    });
  },
  prompt: (options: string | PromptOptions) => {
    if (typeof options === 'string') options = { message: options };
    globalEventBus.emit(EVENTS.PROMPT, { ...options, type: 'prompt' });
    return new Promise((resolve) => {
      globalEventBus.on(EVENTS.FINISH_PROMPT, (result) => resolve(result.result));
    });
  },
});

const mountPlugin = (el: Element) => {
  const app = createApp(PromptPluginContainer);
  app.mount(el);
  return {
    app,
    plugin: createPromptInstance(),
  };
};

export const usePrompts = () => {
  const plugin = inject(PROMPT_INJECTION_KEY);
  return !getCurrentInstance() || !plugin ? createPromptInstance() : plugin;
};
